import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import { message } from "utils/ciq-message";
import DisplayNameListPopover from "components/bulk-edit-material/display-name-list";
import { ProjectContext } from "context/ProjectProvider";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import { useContext, useMemo, useState } from "react";
import { MUTATION_UPDATE_TRACKING } from "../queries/mutation";

export type MoveUntrackedSubmittalModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  featureInstances: any[];
};

function hasLinkedItems(row: any): boolean {
  const isLinkedActivities =
    (row?.tasks?.filter((task: any) => !task.deleted_from_source)?.length ??
      0) > 0;
  const isLinkedRealMaterials =
    (row?.linked_items?.filter((item: any) => !item.implicit).length ?? 0) > 0;

  return isLinkedActivities || isLinkedRealMaterials;
}

export function isSubmittalSkippedForUntrack(row: any): boolean {
  return hasLinkedItems(row) || !!row?.date_block?.final_deadline_date;
}

function toDisplayNameList(rows: any[]) {
  return rows.map((row) => ({
    id: row.id,
    name: row.title ?? row.name ?? ""
  }));
}

function MoveUntrackedSubmittalModal({
  open,
  onClose,
  onSuccess,
  featureInstances
}: MoveUntrackedSubmittalModalProps) {
  const [loading, setLoading] = useState(false);
  const { gqlClientForProject } = useContext(ProjectContext);

  const [updateTrackingMutation] = useCIQMutation(MUTATION_UPDATE_TRACKING, {
    client: gqlClientForProject
  });

  const skippedSubmittals = useMemo(
    () => featureInstances.filter(isSubmittalSkippedForUntrack),
    [featureInstances]
  );

  const skippedCount = skippedSubmittals.length;
  const hasEligibleSubmittals = featureInstances.length > skippedCount;

  const handleStopTracking = async () => {
    if (!featureInstances.length) return;
    setLoading(true);
    const featureInstanceIds = featureInstances.map((instance) => instance.id);
    const response = await updateTrackingMutation({
      variables: {
        featureInstanceIds,
        isTracked: false
      }
    });

    if (response.data?.update_feature_tracking) {
      message.success("Submittal moved to Not Tracked list.");
      onSuccess();
    } else {
      message.error("Failed to move submittal to Not Tracked list");
    }
    setLoading(false);
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <ExclamationCircleOutlined className="text-ciq-warning-text text-2xl" />
          <span>Do Not Track?</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      onOk={handleStopTracking}
      okText="Confirm"
      cancelText="Cancel"
      okButtonProps={{
        loading,
        disabled: loading || !hasEligibleSubmittals
      }}
      cancelButtonProps={{ disabled: loading }}
      mask={{ closable: !loading }}
      destroyOnHidden
      width={450}
      data-testid="move-untracked-submittal-modal"
    >
      <p className="m-0 text-black text-opacity-80">
        You can no longer plan or track{" "}
        {featureInstances.length === 1 ? "this Submittal" : "these Submittals"}{" "}
        for Material procurement.
      </p>

      <p className="m-0 text-black text-opacity-80">
        If needed, you can move it back to the Submittal Log and track it.
      </p>

      {skippedCount > 0 && (
        <div className="mt-4 flex items-start gap-2 text-xs px-3 py-2 border border-solid border-ciq-warning-border bg-ciq-warning-bg rounded">
          <div className="text-black text-opacity-80">
            <DisplayNameListPopover
              list={toDisplayNameList(skippedSubmittals)}
              placement="left"
            >
              <span className="underline text-black text-opacity-90 font-semibold">
                {skippedCount}
              </span>
            </DisplayNameListPopover>
            out of {featureInstances.length} Submittal
            {skippedCount === 1 ? "" : "s"} have links or deadline and will
            remain in the Submittal Log. Remove them to stop tracking
          </div>
        </div>
      )}
    </Modal>
  );
}

export default MoveUntrackedSubmittalModal;
